import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FileDown,
  LogOut,
  User,
  Plus,
  ArrowRight,
  GitCompare,
  ShieldAlert,
  Trash2,
  RotateCcw,
  AlertTriangle,
  Archive,
  ClipboardList,
  X,
  CheckSquare,
  Clock,
  PlayCircle,
  Eye,
  Sparkles,
  Compass,
  Target,
  Wrench,
} from "lucide-react";
import { api, clearSession, getUser } from "@/lib/api";
import Footer from "@/components/Footer";
import { downloadPdfWithProgress } from "@/lib/pdfDownload";
import BrandHeader from "@/components/BrandHeader";
import { DashboardBanner } from "@/components/DashboardBanner";
import Consulting from "@/pages/Consulting";
import PdfPreviewModal from "@/components/PdfPreviewModal";
import OnboardingTour, { shouldShowOnboarding } from "@/components/OnboardingTour";
import NextStepCard from "@/components/NextStepCard";
import { TESTIDS } from "@/constants/testIds";
import { formatDate, levelBadgeClass } from "@/lib/format";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Dashboard() {
  const navigate = useNavigate();
  const user = getUser();
  const [tab, setTab] = useState("active"); // "active" | "trash"
  const [items, setItems] = useState([]);
  const [trash, setTrash] = useState([]);
  const [draft, setDraft] = useState(null);
  const [retentionDays, setRetentionDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [selectedActive, setSelectedActive] = useState([]);
  const [selectedTrash, setSelectedTrash] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [previewFilename, setPreviewFilename] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [hasConsulting, setHasConsulting] = useState(true); // asumimos true para no parpadear
  const [thcExpanded, setThcExpanded] = useState(() => localStorage.getItem("dash_expand_thc") === "1");
  const [consultingExpanded, setConsultingExpanded] = useState(() => localStorage.getItem("dash_expand_consulting") === "1");
  const [consultingSummary, setConsultingSummary] = useState(null);
  const toggleThc = () => setThcExpanded((v) => {
    localStorage.setItem("dash_expand_thc", v ? "0" : "1");
    if (v) setTab("active");
    return !v;
  });
  const toggleConsulting = () => setConsultingExpanded((v) => {
    localStorage.setItem("dash_expand_consulting", v ? "0" : "1");
    return !v;
  });
  // confirm: { kind: "soft"|"permanent"|"bulk-soft"|"bulk-permanent"|"discard-draft", ids?: [], id?, label?, count? }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, t, d, cs] = await Promise.all([
        api.get("/diagnostic/list"),
        api.get("/diagnostic/trash"),
        api.get("/diagnostic/draft"),
        api.get("/consulting/status").catch(() => ({ data: { has_consulting_access: true } })),
      ]);
      setItems(a.data.items);
      setTrash(t.data.items);
      setRetentionDays(t.data.retention_days || 30);
      setDraft(d.data?.draft || null);
      setHasConsulting(!!cs.data?.has_consulting_access);
    } catch (_err) {
      toast.error("No pudimos cargar tus diagnósticos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Muestra el tour de onboarding la primera vez que un usuario ve el dashboard
  // sin diagnósticos ni borrador. Espera a que cargue para no parpadear.
  useEffect(() => {
    if (loading) return;
    const hasAny = items.length > 0 || trash.length > 0 || !!draft;
    if (shouldShowOnboarding({ hasAny })) setShowOnboarding(true);
  }, [loading, items.length, trash.length, draft]);

  function logout() {
    clearSession();
    navigate("/");
  }

  function toggleSelectActive(id) {
    setSelectedActive((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 5) {
        toast.error("Máximo 5 diagnósticos por comparativa");
        return prev;
      }
      return [...prev, id];
    });
  }
  function toggleSelectTrash(id) {
    setSelectedTrash((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  const allActiveSelected = useMemo(
    () => items.length > 0 && selectedActive.length === items.length,
    [items, selectedActive]
  );
  const allTrashSelected = useMemo(
    () => trash.length > 0 && selectedTrash.length === trash.length,
    [trash, selectedTrash]
  );

  function toggleAllActive() {
    setSelectedActive(allActiveSelected ? [] : items.map((i) => i.id));
  }
  function toggleAllTrash() {
    setSelectedTrash(allTrashSelected ? [] : trash.map((i) => i.id));
  }

  // Saludo y nombre para la cabecera del dashboard.
  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 6) return "Buenas noches";
    if (h < 13) return "Buenos días";
    if (h < 20) return "Buenas tardes";
    return "Buenas noches";
  }, []);
  const firstName = useMemo(() => {
    const raw = (user?.email || "").split("@")[0] || "";
    const clean = raw.replace(/[._-]+/g, " ").replace(/[0-9]+$/g, "").trim();
    return clean ? clean.charAt(0).toUpperCase() + clean.slice(1) : "hola";
  }, [user?.email]);

  async function downloadPdf(id) {
    setDownloadingId(id);
    try {
      await downloadPdfWithProgress({
        url: `/diagnostic/${id}/pdf`,
        filename: `talent-health-check.pdf`,
        kind: "std",
        label: "Preparando tu informe",
      });
    } catch (err) {
      console.error("PDF download failed", {
        status: err?.response?.status,
        code: err?.code,
        message: err?.message,
      });
      if (err?.code === "ECONNABORTED" || err?.message?.includes("timeout")) {
        toast.error("La generación tardó demasiado. Vuelve a intentarlo en unos segundos.");
      } else {
        toast.error("No pudimos generar el PDF");
      }
    } finally {
      setDownloadingId(null);
    }
  }

  async function softDelete(id) {
    try {
      await api.delete(`/diagnostic/${id}`);
      toast.success("Diagnóstico enviado a la papelera");
      setSelectedActive((s) => s.filter((x) => x !== id));
      await load();
    } catch (_e) {
      toast.error("No pudimos enviarlo a la papelera");
    }
  }

  async function bulkSoftDelete(ids) {
    try {
      const { data } = await api.post("/diagnostic-bulk/delete", { ids });
      toast.success(
        `${data.affected} diagnóstico${data.affected === 1 ? "" : "s"} enviado${
          data.affected === 1 ? "" : "s"
        } a la papelera`
      );
      setSelectedActive([]);
      await load();
    } catch (_e) {
      toast.error("No pudimos completar la acción");
    }
  }

  async function restore(id) {
    try {
      await api.post(`/diagnostic/${id}/restore`);
      toast.success("Diagnóstico restaurado");
      await load();
    } catch (_e) {
      toast.error("No pudimos restaurar el diagnóstico");
    }
  }

  async function bulkRestore(ids) {
    try {
      const { data } = await api.post("/diagnostic-bulk/restore", { ids });
      toast.success(
        `${data.affected} diagnóstico${data.affected === 1 ? "" : "s"} restaurado${
          data.affected === 1 ? "" : "s"
        }`
      );
      setSelectedTrash([]);
      await load();
    } catch (_e) {
      toast.error("No pudimos restaurar la selección");
    }
  }

  async function permanentDelete(id) {
    try {
      await api.delete(`/diagnostic/${id}/permanent`);
      toast.success("Diagnóstico eliminado definitivamente");
      await load();
    } catch (_e) {
      toast.error("No pudimos eliminar el diagnóstico");
    }
  }

  async function bulkPermanentDelete(ids) {
    try {
      const { data } = await api.post("/diagnostic-bulk/permanent", { ids });
      toast.success(
        `${data.affected} diagnóstico${data.affected === 1 ? "" : "s"} eliminado${
          data.affected === 1 ? "" : "s"
        } definitivamente`
      );
      setSelectedTrash([]);
      await load();
    } catch (_e) {
      toast.error("No pudimos completar la eliminación");
    }
  }

  async function confirmAction() {
    if (!confirm) return;
    if (confirm.kind === "soft") await softDelete(confirm.id);
    else if (confirm.kind === "permanent") await permanentDelete(confirm.id);
    else if (confirm.kind === "bulk-soft") await bulkSoftDelete(confirm.ids);
    else if (confirm.kind === "bulk-permanent") await bulkPermanentDelete(confirm.ids);
    else if (confirm.kind === "discard-draft") await discardDraft();
    setConfirm(null);
  }

  async function discardDraft() {
    try {
      await api.delete("/diagnostic/draft");
      toast.success("Diagnóstico sin terminar descartado");
      setDraft(null);
      await load();
    } catch (_e) {
      toast.error("No pudimos descartar el diagnóstico");
    }
  }

  const visibleItems = thcExpanded ? items : items.slice(0, draft ? 1 : 2);
  useEffect(() => {
    if (loading || window.location.hash !== "#consultorias") return;
    const t = setTimeout(() => document.getElementById("consultorias")?.scrollIntoView({ behavior: "smooth" }), 400);
    return () => clearTimeout(t);
  }, [loading]);
  const consultingPending = [
    consultingSummary?.running ? `${consultingSummary.running} Informe en proceso de análisis…` : null,
    consultingSummary?.drafts ? `${consultingSummary.drafts} ${consultingSummary.drafts === 1 ? "Borrador" : "Borradores"}` : null,
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid={TESTIDS.dashboard.root}>
      <BrandHeader
        right={
          <div className="flex items-center gap-2 md:gap-3 min-w-0 shrink">
            {user?.is_admin && (
              <Link to="/admin" className="btn-ghost shrink-0 px-2 sm:px-3" title="Panel admin" aria-label="Panel admin" data-testid="dashboard-admin-link">
                <ShieldAlert className="w-4 h-4" strokeWidth={1.75} /> <span className="hidden lg:inline">Panel admin</span>
              </Link>
            )}
            <Link to="/cuenta" className="btn-ghost shrink-0 px-2 sm:px-3" title="Mi cuenta" aria-label="Mi cuenta" data-testid="dashboard-account-link">
              <User className="w-4 h-4" strokeWidth={1.75} /> <span className="hidden lg:inline">Cuenta</span>
            </Link>
            <div className="hidden lg:flex flex-col items-end leading-tight min-w-0">
              <span className="text-[11px] text-gray-400 uppercase tracking-wider">Sesión</span>
              <span className="text-sm text-gray-700 font-medium truncate max-w-[180px] xl:max-w-[240px]">{user?.email}</span>
            </div>
            <button className="btn-ghost shrink-0 px-2 sm:px-3" onClick={logout} data-testid={TESTIDS.dashboard.logoutBtn} title="Salir" aria-label="Salir">
              <LogOut className="w-4 h-4" strokeWidth={1.75} /> <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        }
      />

      <main className="max-w-6xl mx-auto px-6 md:px-10 pt-14 pb-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 fade-in-up">
          <div>
            <div className="eyebrow mb-4">Tu área personal</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
              {greeting},{" "}
              <span className="text-[#0f6e56]">{firstName}</span>.
            </h1>
            <p className="text-slate-500 mt-2 lg:whitespace-nowrap">
              Aquí tienes tus diagnósticos{hasConsulting ? " y tus consultorías" : ""}. Pulsa «Ver todos» para ver el historial completo, comparar o gestionar la papelera.
            </p>
          </div>
        </div>

        <DashboardBanner
          kind="thc"
          label="Diagnóstico rápido"
          title="Talent Health Check"
          summary={items[0] ? `Último: ${items[0].overall_score}/120 · ${items[0].overall_level}` : "Sin diagnósticos todavía"}
          countLabel={`${items.length} ${items.length === 1 ? "diagnóstico" : "diagnósticos"}`}
          pending={draft ? ["1 No completado"] : []}
          total={items.length + (draft ? 1 : 0)}
          expanded={thcExpanded}
          onToggle={() => toggleThc()}
        >
        <div className="flex justify-start mb-4">
          <button
            className="btn-primary"
            onClick={() => navigate("/diagnostic")}
            data-testid={TESTIDS.dashboard.newDiagnosticBtn}
          >
            <Plus className="w-4 h-4" strokeWidth={2.25} /> Realizar nuevo diagnóstico
          </button>
        </div>
        {thcExpanded && (<>
        {/* KPI strip: 3 métricas resumen. Solo se muestra si hay datos. */}
        {items.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 fade-in-up">
            <div className="rounded-2xl border border-slate-200/70 bg-white p-5">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Diagnósticos</div>
              <div className="flex items-baseline gap-2">
                <span className="font-display text-3xl font-semibold text-slate-900">{items.length}</span>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200/70 bg-white p-5">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Último resultado</div>
              <div className="font-display text-3xl font-semibold text-slate-900">
                {items[0]?.overall_score ?? "—"}
                <span className="text-slate-400 text-lg">/120</span>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200/70 bg-white p-5">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Nivel actual</div>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className={`w-2 h-2 rounded-full ${
                    items[0]?.overall_level === "Sólido"
                      ? "bg-emerald-500"
                      : items[0]?.overall_level === "Funcional"
                      ? "bg-blue-500"
                      : items[0]?.overall_level === "En desarrollo"
                      ? "bg-amber-500"
                      : "bg-red-500"
                  }`}
                ></span>
                <span className="font-display text-xl font-semibold text-slate-900">
                  {items[0]?.overall_level ?? "—"}
                </span>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200/70 bg-white p-5">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Papelera</div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="font-display text-3xl font-semibold text-slate-900">{trash.length}</span>
                <span className="text-slate-400 text-xs">restantes</span>
              </div>
            </div>
          </div>
        )}

        {/* Next Step: tarjeta con la próxima micro-acción del plan del último
            diagnóstico con personalización lista. Se auto-oculta si no hay
            diagnósticos o si la personalización aún está pendiente. */}
        {(() => {
          const latestReady = items.find(
            (it) => !it.personalization_status || it.personalization_status === "ready"
          );
          return latestReady ? (
            <div className="mb-8 fade-in-up">
              <NextStepCard
                latestId={latestReady.id}
                onOpenDetail={(id) => navigate(`/results/${id}`)}
              />
            </div>
          ) : null;
        })()}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 fade-in-up">
          <TabBtn
            active={tab === "active"}
            onClick={() => setTab("active")}
            testId="dashboard-tab-active"
          >
            <ClipboardList className="w-4 h-4" strokeWidth={1.75} />
            Diagnósticos {items.length > 0 && <span className="opacity-70">· {items.length}</span>}
          </TabBtn>
          <TabBtn
            active={tab === "trash"}
            onClick={() => setTab("trash")}
            testId="dashboard-tab-trash"
          >
            <Archive className="w-4 h-4" strokeWidth={1.75} />
            Papelera {trash.length > 0 && <span className="opacity-70">· {trash.length}</span>}
          </TabBtn>
        </div>

        {/* Bulk action bar - ACTIVE tab */}
        {tab === "active" && selectedActive.length > 0 && (
          <BulkBar
            count={selectedActive.length}
            onClear={() => setSelectedActive([])}
            testIdRoot="bulk-active"
          >
            {selectedActive.length >= 2 && selectedActive.length <= 5 && (
              <button
                onClick={() => navigate(`/compare?ids=${selectedActive.join(",")}`)}
                className="btn-primary text-sm py-2 px-5"
                data-testid={TESTIDS.dashboard.compareBtn}
              >
                <GitCompare className="w-4 h-4" /> Comparar {selectedActive.length}
              </button>
            )}
            <button
              onClick={() =>
                setConfirm({
                  kind: "bulk-soft",
                  ids: selectedActive,
                  count: selectedActive.length,
                })
              }
              className="inline-flex items-center gap-2 text-sm py-2 px-5 rounded-full bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors"
              data-testid="bulk-active-delete-btn"
            >
              <Trash2 className="w-4 h-4" strokeWidth={2} />
              Enviar {selectedActive.length} a papelera
            </button>
          </BulkBar>
        )}

        {/* Bulk action bar - TRASH tab */}
        {tab === "trash" && selectedTrash.length > 0 && (
          <BulkBar
            count={selectedTrash.length}
            onClear={() => setSelectedTrash([])}
            testIdRoot="bulk-trash"
          >
            <button
              onClick={() => bulkRestore(selectedTrash)}
              className="btn-secondary text-sm py-2 px-5"
              data-testid="bulk-trash-restore-btn"
            >
              <RotateCcw className="w-4 h-4" strokeWidth={1.75} />
              Restaurar {selectedTrash.length}
            </button>
            <button
              onClick={() =>
                setConfirm({
                  kind: "bulk-permanent",
                  ids: selectedTrash,
                  count: selectedTrash.length,
                })
              }
              className="inline-flex items-center gap-2 text-sm py-2 px-5 rounded-full bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors"
              data-testid="bulk-trash-delete-btn"
            >
              <Trash2 className="w-4 h-4" strokeWidth={2} />
              Eliminar {selectedTrash.length}
            </button>
          </BulkBar>
        )}

        </>)}

        {loading ? (
          <div className="card text-center text-gray-500">Cargando…</div>
        ) : tab === "active" ? (
          <>
            {draft && (
              <div
                className="mb-6 rounded-2xl border-2 border-orange-300 bg-gradient-to-br from-orange-50 to-amber-50 p-5 md:p-6 fade-in-up shadow-[0_8px_28px_-12px_rgba(249,115,22,0.25)]"
                data-testid="dashboard-draft-card"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-orange-500 text-white flex items-center justify-center flex-shrink-0 shadow-md">
                    <Clock className="w-6 h-6" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="badge bg-orange-100 text-orange-800 border border-orange-200">
                        No completado
                      </span>
                      <span className="text-[11px] uppercase tracking-widest text-orange-700 font-semibold">
                        Diagnóstico en progreso
                      </span>
                    </div>
                    <div className="font-display text-lg font-semibold text-gray-900">
                      {draft.answered_count} de {draft.total_questions} preguntas respondidas
                    </div>
                    <div className="mt-2.5 h-2 w-full bg-orange-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-orange-500 to-amber-500 transition-all"
                        style={{
                          width: `${Math.round(
                            (draft.answered_count / draft.total_questions) * 100
                          )}%`,
                        }}
                      />
                    </div>
                    {draft.updated_at && (
                      <div className="text-xs text-orange-800/70 mt-2">
                        Última edición: {formatDate(draft.updated_at)}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => navigate("/diagnostic")}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-semibold text-sm bg-gradient-to-r from-orange-500 to-amber-500 shadow-md hover:-translate-y-0.5 hover:shadow-lg transition-transform"
                      data-testid="dashboard-draft-continue-btn"
                    >
                      <PlayCircle className="w-4 h-4" strokeWidth={2} /> Continuar
                    </button>
                    <button
                      onClick={() => setConfirm({ kind: "discard-draft" })}
                      className="text-sm py-2 px-4 rounded-full border border-orange-200 text-orange-700 hover:bg-orange-100 transition-colors inline-flex items-center gap-2"
                      data-testid="dashboard-draft-discard-btn"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.75} /> Descartar
                    </button>
                  </div>
                </div>
              </div>
            )}
            {items.length === 0 ? (
              <div className="card text-center py-16 fade-in-up">
                <div className="w-16 h-16 rounded-2xl brand-gradient mx-auto flex items-center justify-center text-white shadow-lg mb-6">
                  <Plus className="w-8 h-8" strokeWidth={2} />
                </div>
                <h2 className="font-display text-2xl font-bold text-gray-900 mb-2">
                  {draft
                    ? "Aún no has completado ningún diagnóstico"
                    : "Aún no tienes ningún diagnóstico"}
                </h2>
                <p className="text-gray-500 max-w-md mx-auto mb-8">
                  {draft
                    ? "Termina el diagnóstico que tienes en progreso para ver tu informe completo y tu evolución en las 6 áreas evaluadas."
                    : "Realiza tu primer diagnóstico en pocos minutos. Al terminar recibirás tu informe completo por email y en PDF."}
                </p>
                <button
                  className="btn-primary"
                  onClick={() => navigate("/diagnostic")}
                  data-testid="empty-start-btn"
                >
                  {draft ? "Continuar diagnóstico" : "Empezar diagnóstico"}{" "}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                {thcExpanded && (<SelectAllRow
                  checked={allActiveSelected}
                  onChange={toggleAllActive}
                  label={
                    selectedActive.length > 0
                      ? `${selectedActive.length} de ${items.length} seleccionado${selectedActive.length === 1 ? "" : "s"}`
                      : `Seleccionar todos (${items.length})`
                  }
                  testId="select-all-active"
                />)}
                <div className="space-y-4">
                  {visibleItems.map((it, idx) => {
                    const isSelected = selectedActive.includes(it.id);
                    const lvl = it.overall_level;
                    const lvlColor =
                      lvl === "Sólido" ? "#0F6E56" :
                      lvl === "Funcional" ? "#0891b2" :
                      lvl === "En desarrollo" ? "#b45309" : "#dc2626";
                    const lvlGradient =
                      lvl === "Sólido" ? "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)" :
                      lvl === "Funcional" ? "linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)" :
                      lvl === "En desarrollo" ? "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)" :
                      "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)";
                    const rowNumber = items.length - idx;
                    return (
                      <div
                        key={it.id}
                        className="group relative rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-0.5 fade-in-up"
                        style={{
                          background: isSelected
                            ? "linear-gradient(135deg, rgba(15,110,86,0.06) 0%, rgba(201,168,76,0.05) 100%), linear-gradient(180deg, #ffffff 0%, #fdfcf6 100%)"
                            : "linear-gradient(180deg, #ffffff 0%, #fdfcf6 100%)",
                          boxShadow: isSelected
                            ? "0 22px 45px -25px rgba(15,110,86,0.30), 0 0 0 1.5px rgba(201,168,76,0.55)"
                            : "0 22px 45px -30px rgba(11,61,46,0.28), 0 0 0 1px rgba(11,61,46,0.06)",
                          animationDelay: `${Math.min(idx, 8) * 50}ms`,
                        }}
                        data-testid={TESTIDS.dashboard.diagnosticRow}
                      >
                        {/* Spine vertical por nivel */}
                        <span
                          aria-hidden
                          className="absolute top-0 bottom-0 left-0 w-1 transition-all duration-300 group-hover:w-1.5"
                          style={{
                            background: isSelected
                              ? "linear-gradient(180deg, #C9A84C 0%, #0F6E56 100%)"
                              : `linear-gradient(180deg, ${lvlColor} 0%, ${lvlColor}80 100%)`,
                          }}
                        />
                        {/* Barra dorada superior en hover */}
                        <span
                          aria-hidden
                          className="absolute top-0 left-1 right-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 50%, #C9A84C 100%)" }}
                        />

                        <div className="p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4">
                          {thcExpanded && (<label className="flex items-center gap-3 cursor-pointer shrink-0">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelectActive(it.id)}
                              className="w-5 h-5 rounded border-gray-300 text-[#0f6e56] focus:ring-[#0f6e56] accent-[#0f6e56]"
                              data-testid="dashboard-active-checkbox"
                            />
                          </label>)}

                          {/* Score tile premium */}
                          <button
                            type="button"
                            onClick={() => navigate(`/results/${it.id}`)}
                            className="w-16 h-16 md:w-[68px] md:h-[68px] rounded-2xl flex flex-col items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-[1.05] group-hover:rotate-[-2deg]"
                            style={{
                              background: lvlGradient,
                              boxShadow: `0 10px 24px -8px ${lvlColor}55, inset 0 -1px 0 rgba(255,255,255,0.35), inset 0 1px 0 rgba(255,255,255,0.6)`,
                              border: `1px solid ${lvlColor}30`,
                              color: lvlColor,
                            }}
                            data-testid="dashboard-score-tile"
                            title={`Puntuación: ${it.overall_score}/120`}
                          >
                            <div className="font-display text-2xl font-bold leading-none tabular-nums">
                              {it.overall_score}
                            </div>
                            <div className="text-[9px] uppercase tracking-widest opacity-60 mt-1 font-semibold">
                              /120
                            </div>
                          </button>

                          {/* Título + meta */}
                          <div
                            className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => navigate(`/results/${it.id}`)}
                          >
                            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] font-bold" style={{ color: "#94a3b8" }}>
                              <span style={{ width: 4, height: 4, borderRadius: 999, background: "#C9A84C", display: "inline-block" }} />
                              Diagnóstico · N° {String(rowNumber).padStart(2, "0")}
                            </div>
                            <div
                              className="font-display text-lg md:text-xl font-bold text-slate-900 mt-1 transition-colors group-hover:text-[#0b3d2e]"
                              style={{ letterSpacing: "-0.015em" }}
                            >
                              {formatDate(it.created_at)}
                            </div>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <span
                                className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full"
                                style={{
                                  background: `${lvlColor}12`,
                                  color: lvlColor,
                                  border: `0.5px solid ${lvlColor}30`,
                                }}
                              >
                                <span style={{ width: 5, height: 5, borderRadius: 999, background: lvlColor, display: "inline-block" }} />
                                {it.overall_level}
                              </span>
                              {it.is_test_data && (
                                <span className="chip bg-slate-100 text-slate-500 border-slate-200 text-[10px]">Test admin</span>
                              )}
                            </div>
                          </div>

                          {/* Acciones */}
                          <div className="flex flex-wrap items-center gap-1.5 md:gap-2 w-full md:w-auto justify-start md:justify-end">
                            <button
                              onClick={() => navigate(`/results/${it.id}`)}
                              className="rounded-full px-4 py-2 text-xs md:text-sm font-semibold text-white inline-flex items-center gap-1.5 transition-all hover:-translate-y-0.5"
                              style={{
                                background: "linear-gradient(135deg, #0b3d2e 0%, #14523f 55%, #0F6E56 100%)",
                                boxShadow: "0 6px 18px -8px rgba(11,61,46,0.5)",
                              }}
                              data-testid="dashboard-view-detail-btn"
                            >
                              Ver detalle <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.25} />
                            </button>
                            <button
                              onClick={() => {
                                setPreviewId(it.id);
                                setPreviewFilename(`talent-health-check-${it.created_at?.slice(0, 10)}.pdf`);
                              }}
                              className="w-9 h-9 flex items-center justify-center rounded-full text-slate-500 hover:text-[#0F6E56] hover:bg-[#0F6E56]/8 transition-colors"
                              data-testid="dashboard-preview-pdf-btn"
                              title="Vista previa del informe"
                              aria-label="Vista previa"
                            >
                              <Eye className="w-[18px] h-[18px]" strokeWidth={1.75} />
                            </button>
                            <button
                              onClick={() => downloadPdf(it.id)}
                              disabled={downloadingId === it.id}
                              className="w-9 h-9 flex items-center justify-center rounded-full text-slate-500 hover:text-[#0F6E56] hover:bg-[#0F6E56]/8 transition-colors disabled:opacity-50"
                              data-testid={TESTIDS.dashboard.downloadPdfBtn}
                              title={downloadingId === it.id ? "Generando PDF…" : "Descargar informe PDF"}
                              aria-label="Descargar PDF"
                            >
                              {downloadingId === it.id ? (
                                <span className="w-4 h-4 border-2 border-[#0F6E56] border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <FileDown className="w-[18px] h-[18px]" strokeWidth={1.75} />
                              )}
                            </button>
                            <button
                              onClick={() =>
                                setConfirm({
                                  kind: "soft",
                                  id: it.id,
                                  label: formatDate(it.created_at),
                                })
                              }
                              className="w-9 h-9 flex items-center justify-center rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                              data-testid="dashboard-trash-btn"
                              aria-label="Enviar a la papelera"
                              title="Enviar a la papelera"
                            >
                              <Trash2 className="w-[18px] h-[18px]" strokeWidth={1.75} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        ) : trash.length === 0 ? (
          <div className="card text-center py-16 fade-in-up" data-testid="dashboard-trash-empty">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 text-gray-400 mx-auto flex items-center justify-center mb-6">
              <Archive className="w-7 h-7" strokeWidth={1.75} />
            </div>
            <h2 className="font-display text-2xl font-bold text-gray-900 mb-2">Papelera vacía</h2>
            <p className="text-gray-500 max-w-md mx-auto">
              Los diagnósticos que envíes a la papelera se guardarán aquí durante{" "}
              <strong>{retentionDays} días</strong> antes de eliminarse definitivamente. Mientras
              tanto puedes restaurarlos con un clic.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-4 rounded-2xl bg-amber-50 border border-amber-100 px-5 py-3 flex items-start gap-3 fade-in-up">
              <AlertTriangle
                className="w-4 h-4 text-amber-700 mt-0.5 shrink-0"
                strokeWidth={2}
              />
              <p className="text-sm text-amber-900" style={{ textAlign: "left" }}>
                Los diagnósticos se conservan aquí durante <strong>{retentionDays} días</strong>.
                Después se eliminan definitivamente sin posibilidad de recuperación.
              </p>
            </div>
            <SelectAllRow
              checked={allTrashSelected}
              onChange={toggleAllTrash}
              label={
                selectedTrash.length > 0
                  ? `${selectedTrash.length} de ${trash.length} seleccionado${selectedTrash.length === 1 ? "" : "s"}`
                  : `Seleccionar todos (${trash.length})`
              }
              testId="select-all-trash"
            />
            <div className="space-y-4">
              {trash.map((it, idx) => {
                const isSelected = selectedTrash.includes(it.id);
                return (
                  <div
                    key={it.id}
                    className={`rounded-2xl border bg-white p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 fade-in-up transition-all ${
                      isSelected
                        ? "border-red-300 shadow-[0_8px_28px_-10px_rgba(239,68,68,0.25)]"
                        : "border-gray-100"
                    }`}
                    style={{ animationDelay: `${Math.min(idx, 8) * 60}ms` }}
                    data-testid="dashboard-trash-row"
                  >
                    <label className="flex items-center gap-3 cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelectTrash(it.id)}
                        className="w-5 h-5 rounded border-gray-300 text-red-500 focus:ring-red-500 accent-red-500"
                        data-testid="dashboard-trash-checkbox"
                      />
                    </label>
                    <div className="w-10 h-10 rounded-xl bg-red-50 text-red-500 flex items-center justify-center shrink-0">
                      <Trash2 className="w-5 h-5" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] uppercase tracking-widest text-gray-400">
                        Diagnóstico del {formatDate(it.created_at)}
                      </div>
                      <div className="font-display text-lg font-semibold text-gray-900 mt-0.5">
                        {it.overall_score}/120 ·{" "}
                        <span className={levelBadgeClass(it.overall_level)}>
                          {it.overall_level}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1.5">
                        {it.days_remaining === 0 ? (
                          <span className="text-red-600 font-semibold">
                            Se eliminará hoy definitivamente
                          </span>
                        ) : (
                          <>
                            Quedan <strong className="text-gray-700">{it.days_remaining}</strong>{" "}
                            {it.days_remaining === 1 ? "día" : "días"} antes de eliminación
                            definitiva
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => restore(it.id)}
                        className="btn-secondary text-sm py-2 px-4"
                        data-testid="trash-restore-btn"
                      >
                        <RotateCcw className="w-4 h-4" strokeWidth={1.75} /> Restaurar
                      </button>
                      <button
                        onClick={() =>
                          setConfirm({
                            kind: "permanent",
                            id: it.id,
                            label: formatDate(it.created_at),
                          })
                        }
                        className="text-sm py-2 px-4 rounded-full border border-red-200 text-red-600 hover:bg-red-50 transition-colors inline-flex items-center gap-2"
                        data-testid="trash-permanent-delete-btn"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.75} /> Eliminar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        </DashboardBanner>

        {!hasConsulting && !loading && (
          <div className="mb-10 fade-in-up" data-testid="dashboard-consulting-premium-banner">
            <div
              className="relative overflow-hidden rounded-3xl group"
              style={{
                background:
                  "radial-gradient(120% 140% at 100% 0%, rgba(201,168,76,0.35) 0%, rgba(11,61,46,0) 55%), radial-gradient(90% 120% at 0% 100%, rgba(94,234,212,0.14) 0%, rgba(11,61,46,0) 60%), linear-gradient(135deg, #0b3d2e 0%, #0F6E56 55%, #145a48 100%)",
                boxShadow: "0 24px 60px -30px rgba(11,61,46,0.55), 0 0 0 1px rgba(201,168,76,0.35), inset 0 0 0 1px rgba(255,255,255,0.05)",
              }}
            >
              {/* Grain / textura sutil */}
              <div
                className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
                }}
              />

              {/* Rayo dorado decorativo esquina superior derecha */}
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-40 blur-3xl pointer-events-none" style={{ background: "#C9A84C" }} />
              {/* Esquina inferior izquierda esmeralda */}
              <div className="absolute -bottom-24 -left-20 w-72 h-72 rounded-full opacity-25 blur-3xl pointer-events-none" style={{ background: "#0F6E56" }} />

              <div className="relative px-6 sm:px-9 py-8 sm:py-9">
                {/* Cabecera con kicker + tag Premium */}
                <div className="flex items-center gap-3 mb-5">
                  <span
                    className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.28em]"
                    style={{ color: "#E5C874" }}
                  >
                    <Sparkles className="w-3.5 h-3.5" strokeWidth={2.4} /> Servicio premium
                  </span>
                  <span className="h-px flex-1 max-w-24" style={{ background: "linear-gradient(90deg, rgba(201,168,76,0.55), transparent)" }} />
                  <span
                    className="text-[10px] font-bold uppercase tracking-[0.24em] px-2.5 py-1 rounded-full"
                    style={{
                      background: "rgba(201,168,76,0.14)",
                      color: "#E5C874",
                      border: "1px solid rgba(201,168,76,0.45)",
                    }}
                  >
                    Consultoría
                  </span>
                </div>

                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                  <div className="max-w-2xl">
                    <h3
                      className="font-display text-2xl sm:text-3xl font-bold leading-tight tracking-tight mb-3"
                      style={{ color: "#F8F5E8" }}
                    >
                      Consultoría de{" "}
                      <span style={{ color: "#E5C874" }}>Gestión del Talento</span>
                    </h3>
                    <p className="text-[15px] leading-relaxed" style={{ color: "rgba(240,253,244,0.82)" }}>
                      Análisis profundo aplicado con nuestra metodología. Informe personalizado,
                      plan de acción a 30, 90 y 180 días y un set de herramientas de gestión listas
                      para desplegar en tu empresa.
                    </p>
                  </div>

                  <div className="flex flex-col items-start lg:items-end gap-3 flex-shrink-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "rgba(229,200,116,0.75)" }}>
                        Desde
                      </span>
                      <span className="font-display text-3xl font-bold" style={{ color: "#E5C874" }}>
                        397&nbsp;€
                      </span>
                    </div>
                    <button
                      onClick={() => navigate("/consulting")}
                      className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold whitespace-nowrap transition-all duration-300 group/btn hover:scale-[1.02]"
                      style={{
                        background: "linear-gradient(135deg, #E5C874 0%, #C9A84C 100%)",
                        color: "#0b3d2e",
                        boxShadow: "0 8px 24px -8px rgba(201,168,76,0.6), inset 0 1px 0 rgba(255,255,255,0.35)",
                      }}
                      data-testid="dashboard-consulting-cta"
                    >
                      Descubrir el servicio
                      <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" strokeWidth={2.5} />
                    </button>
                  </div>
                </div>

                {/* Mini-features */}
                <div className="mt-7 pt-5 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5"
                     style={{ borderTop: "1px solid rgba(201,168,76,0.18)" }}>
                  {[
                    { icon: Compass, title: "Análisis experto", desc: "Metodología propia sobre 6 áreas críticas" },
                    { icon: Target, title: "Plan 30·90·180", desc: "Hoja de ruta priorizada y accionable" },
                    { icon: Wrench, title: "7 herramientas", desc: "Piezas operativas adaptadas a tu contexto" },
                  ].map((f, i) => {
                    const Icon = f.icon;
                    return (
                      <div key={i} className="flex items-start gap-3">
                        <div
                          className="w-9 h-9 rounded-xl inline-flex items-center justify-center flex-shrink-0"
                          style={{
                            background: "rgba(201,168,76,0.12)",
                            border: "1px solid rgba(201,168,76,0.32)",
                            color: "#E5C874",
                          }}
                        >
                          <Icon className="w-[18px] h-[18px]" strokeWidth={2.2} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-[13px] font-semibold" style={{ color: "#F8F5E8" }}>{f.title}</div>
                          <div className="text-[12px] leading-snug" style={{ color: "rgba(240,253,244,0.62)" }}>
                            {f.desc}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {hasConsulting && (
          <DashboardBanner
            kind="consulting"
            label="Consultoría premium"
            title="Tus consultorías"
            summary="Informe, checklist y herramientas de gestión"
            countLabel={consultingSummary ? `${consultingSummary.count} ${consultingSummary.count === 1 ? "consultoría" : "consultorías"}` : ""}
            pending={consultingPending}
            total={consultingSummary?.count || 0}
            expanded={consultingExpanded}
            onToggle={() => toggleConsulting()}
          >
            <Consulting embedded expanded={consultingExpanded} onSummary={setConsultingSummary} />
          </DashboardBanner>
        )}
      </main>
      <Footer />

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="dashboard-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.kind === "soft" && "Enviar diagnóstico a la papelera"}
              {confirm?.kind === "permanent" && "Eliminar definitivamente"}
              {confirm?.kind === "bulk-soft" &&
                `Enviar ${confirm?.count} diagnósticos a la papelera`}
              {confirm?.kind === "bulk-permanent" &&
                `Eliminar ${confirm?.count} diagnósticos definitivamente`}
              {confirm?.kind === "discard-draft" && "Descartar diagnóstico sin terminar"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.kind === "soft" && (
                <>
                  El diagnóstico del <strong>{confirm?.label}</strong> se moverá a la papelera y
                  se guardará durante <strong>{retentionDays} días</strong>. Podrás restaurarlo
                  en cualquier momento durante ese periodo.
                </>
              )}
              {confirm?.kind === "permanent" && (
                <>
                  El diagnóstico del <strong>{confirm?.label}</strong> se eliminará
                  definitivamente. Esta acción no se puede deshacer.
                </>
              )}
              {confirm?.kind === "bulk-soft" && (
                <>
                  Los <strong>{confirm?.count}</strong> diagnósticos seleccionados se moverán a
                  la papelera y se conservarán durante <strong>{retentionDays} días</strong>.
                  Podrás restaurarlos individualmente o todos a la vez durante ese periodo.
                </>
              )}
              {confirm?.kind === "bulk-permanent" && (
                <>
                  Los <strong>{confirm?.count}</strong> diagnósticos seleccionados se eliminarán
                  definitivamente. Esta acción no se puede deshacer.
                </>
              )}
              {confirm?.kind === "discard-draft" && (
                <>
                  Perderás las respuestas que hayas guardado hasta ahora en el diagnóstico en
                  progreso. Esta acción no se puede deshacer, pero podrás empezar un diagnóstico
                  nuevo cuando quieras.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="dashboard-confirm-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmAction}
              data-testid="dashboard-confirm-ok"
              className={
                confirm?.kind === "permanent" ||
                confirm?.kind === "bulk-permanent" ||
                confirm?.kind === "discard-draft"
                  ? "bg-red-600 hover:bg-red-700 focus:ring-red-600"
                  : ""
              }
            >
              {confirm?.kind === "soft" && "Enviar a papelera"}
              {confirm?.kind === "permanent" && "Eliminar definitivamente"}
              {confirm?.kind === "bulk-soft" && `Enviar ${confirm?.count} a papelera`}
              {confirm?.kind === "bulk-permanent" &&
                `Eliminar ${confirm?.count} definitivamente`}
              {confirm?.kind === "discard-draft" && "Descartar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {previewId && (
        <PdfPreviewModal
          diagnosticId={previewId}
          filename={previewFilename}
          onClose={() => {
            setPreviewId(null);
            setPreviewFilename(null);
          }}
        />
      )}
      {showOnboarding && <OnboardingTour onDone={() => setShowOnboarding(false)} />}
    </div>
  );
}

function BulkBar({ count, onClear, children, testIdRoot }) {
  return (
    <div
      className="mb-6 rounded-2xl bg-white border border-[#0f6e56]/30 shadow-[0_10px_30px_-12px_rgba(15,110,86,0.35)] px-5 py-3 flex flex-col md:flex-row md:items-center gap-3 fade-in-up sticky top-20 z-20"
      data-testid={`${testIdRoot}-bar`}
    >
      <div className="flex items-center gap-3 flex-1">
        <div className="w-9 h-9 rounded-xl brand-gradient text-white flex items-center justify-center font-bold shrink-0">
          {count}
        </div>
        <div className="text-sm text-gray-700 font-medium">
          {count === 1 ? "1 diagnóstico seleccionado" : `${count} diagnósticos seleccionados`}
        </div>
        <button
          onClick={onClear}
          className="btn-ghost text-xs ml-auto md:ml-2"
          data-testid={`${testIdRoot}-clear`}
        >
          <X className="w-3.5 h-3.5" /> Deseleccionar
        </button>
      </div>
      <div className="flex items-center gap-2 flex-wrap justify-end">{children}</div>
    </div>
  );
}

function SelectAllRow({ checked, onChange, label, testId }) {
  return (
    <label
      className="flex items-center gap-3 px-2 py-2 mb-3 cursor-pointer text-sm text-gray-600 hover:text-gray-900 transition-colors"
      data-testid={testId}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="w-4 h-4 rounded border-gray-300 accent-[#0f6e56]"
      />
      <CheckSquare className="w-3.5 h-3.5 opacity-60" strokeWidth={1.75} />
      <span className="font-medium">{label}</span>
    </label>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-5 py-2 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-2 ${
        active
          ? "brand-gradient text-white shadow-md"
          : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
      }`}
    >
      {children}
    </button>
  );
}
